import { useId, type CSSProperties } from 'react';
import { AVATAR_HUES, AVATAR_PRESETS, type AvatarKind } from '@circles/convex';
import { avatarTint } from './avatarTint';
import p from './AvatarPicker.module.css';

const GROUPS = [
  { group: 'people', title: 'People' },
  { group: 'animals', title: 'Animals' },
] as const;

/** The preset id behind a member's `avatarUrl` when they picked one (`/avatars/<id>.svg`), else null. */
export function presetOf(avatarUrl: string | undefined, avatarKind: AvatarKind | undefined): string | null {
  if (avatarKind !== 'preset' || avatarUrl === undefined) return null;
  return /^\/avatars\/([a-z0-9-]+)\.svg$/.exec(avatarUrl)?.[1] ?? null;
}

/**
 * The background swatches (`AVATAR_HUES`) as one radio group. A hue the member never picked (the
 * one hashed from their id) is usually not in the palette, so then no swatch is checked.
 */
export function AvatarHuePicker({
  name,
  value,
  onChange,
  disabled = false,
}: {
  name: string;
  value: number;
  onChange: (hue: number) => void;
  disabled?: boolean;
}) {
  const groupName = useId();
  return (
    <div className={p.group}>
      <div className={p.title} aria-hidden>
        Background
      </div>
      <div
        className={p.hues}
        style={{ '--hue-count': AVATAR_HUES.length } as CSSProperties}
        role="radiogroup"
        aria-label={`Background color for ${name}`}
        data-testid="avatar-hue-picker"
      >
        {AVATAR_HUES.map(({ hue, label }) => (
          <label key={hue} className={p.hue} title={label}>
            <input
              className={p.input}
              type="radio"
              name={groupName}
              value={hue}
              checked={value === hue}
              disabled={disabled}
              aria-label={label}
              onChange={() => onChange(hue)}
            />
            <span className={p.swatch} style={{ background: avatarTint(hue) }} />
          </label>
        ))}
      </div>
    </div>
  );
}

/**
 * The illustrated avatar library as one radio group (arrow keys move through it), people then
 * animals, each tile on the member's tint (`background`) so the pick previews as it will show.
 * Controlled: `onChange` fires on every selection and the caller saves it.
 */
export function AvatarPicker({
  name,
  value,
  onChange,
  background,
  disabled = false,
  className,
}: {
  name: string;
  value: string | null;
  onChange: (preset: string) => void;
  background: string;
  disabled?: boolean;
  className?: string | undefined;
}) {
  const groupName = useId();
  return (
    <div className={className ? `${p.root} ${className}` : p.root} role="radiogroup" aria-label={`Picture for ${name}`} data-testid="avatar-picker">
      {GROUPS.map(({ group, title }) => (
        <div key={group} className={p.group}>
          <div className={p.title} aria-hidden>
            {title}
          </div>
          <div className={p.grid}>
            {AVATAR_PRESETS.filter((a) => a.group === group).map((a) => (
              <label key={a.id} className={p.tile} title={a.label}>
                <input
                  className={p.input}
                  type="radio"
                  name={groupName}
                  value={a.id}
                  checked={value === a.id}
                  disabled={disabled}
                  aria-label={a.label}
                  onChange={() => onChange(a.id)}
                />
                <img className={p.img} src={`/avatars/${a.id}.svg`} alt="" loading="lazy" decoding="async" style={{ background }} />
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
